import React, { useState, useEffect, useContext } from 'react';
import { useParams } from 'react-router-dom';

import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';

import CheckboxGroup from '../../../sharedModules/layout/CheckboxGroup';
import ErrorMessage from '../../../sharedModules/layout/ErrorMessage';
import LoadingWrapper from '../../../sharedModules/layout/LoadingWrapper';
import PageHeader from '../../../sharedModules/layout/PageHeader';

import { SelectOption } from '../../../sharedModules/form/model';
import { minimumLoading } from '../../../utils/minimumLoading';
import { SnackBarContext } from '../../../sharedModules/form/contexts/SnackBarContext';

import axios from '../../../sharedModules/utils/axios.utils';

const NotificationSettingDetailPage: React.FC = () => {
  const { notificationSettingId } = useParams<{ notificationSettingId: string }>();
  const { showSuccessSnackBar, showErrorSnackBar } = useContext(SnackBarContext);

  const [notificationError, setNotificationError] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [testLoading, setTestLoading] = useState<boolean>(false);
  const [notificationSetting, setNotifcationSetting] = useState<any>({});
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [userOptionList, setUserOptionList] = useState<SelectOption[]>([]);
  const [appModuleId, setAppModuleId] = useState<number | ''>('');
  const [appModuleOptions, setAppModuleOptions] = useState<SelectOption[]>([]);

  const fetchNotificationSetting = async () => {
    try {
      const response: any = await axios.get(`/admin/notification_settings/${notificationSettingId}`);
      setNotifcationSetting(response.data.result);
      setSelectedUserIds(response.data.result.user_ids);
      setUserOptionList(response.data.user_options);
      setAppModuleId(response.data.result.app_module_id || '');
      setAppModuleOptions(response.data.app_module_options);
      setNotificationError('');
    } catch {
      setNotificationError('Something went wrong getting the Notification Setting');
    }
  };

  useEffect(() => {
    void minimumLoading(async () => {
      await fetchNotificationSetting();
    }, setLoading);
  }, [notificationSettingId]);

  const submitUpdatedIds = async () => {
    await axios.put(`/admin/notification_settings/${notificationSettingId}`, {
      notification_setting: {
        app_module_id: appModuleId || null,
        user_ids: selectedUserIds,
      },
    });
  };

  const handleUpdateSetting = async () => {
    try {
      await minimumLoading(async () => {
        await submitUpdatedIds();
        await fetchNotificationSetting();
      }, setLoading);

      setNotificationError('');
      showSuccessSnackBar?.('Recipients updated successfully');
    } catch {
      setNotificationError('Something went wrong updating the Notification Setting');
      showErrorSnackBar?.('Failed to update recipients');
    }
  };

  const handleTestNotification = async () => {
    setTestLoading(true);
    try {
      await axios.post(`/admin/notification_settings/${notificationSettingId}/test`);
      showSuccessSnackBar?.('Test email sent to your email address');
    } catch (e: any) {
      showErrorSnackBar?.(e?.response?.data?.error || 'Failed to send test email');
    } finally {
      setTestLoading(false);
    }
  };

  return (
    <LoadingWrapper isLoading={loading} message="Loading...">
      <Grid container spacing={2} style={{ padding: '1% 2%' }}>
        <Grid item xs={12}>
          <PageHeader
            mainTitle={notificationSetting?.name}
            subTitle="Notification Setting Detail"
            backButtonLabel="Notification Settings"
            backButtonLink="/admin/notification_settings"
          />
        </Grid>
        <Grid item xs={12}>
          <ErrorMessage message={notificationError} />
        </Grid>
        <Grid item xs={12}>
          <FormControl fullWidth>
            <InputLabel id="app-module-label">App Module</InputLabel>
            {/* Alphabetically sorted by backend. */}
            <Select
              labelId="app-module-label"
              value={appModuleId}
              label="App Module"
              onChange={(e) => setAppModuleId(e.target.value as number | '')}
            >
              <MenuItem value="">
                <em>None (All Users)</em>
              </MenuItem>
              {appModuleOptions?.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <CheckboxGroup
            selectedIds={selectedUserIds}
            setSelectedIds={setSelectedUserIds}
            optionsList={userOptionList}
          />
        </Grid>
        <Grid item xs={12} style={{ display: 'flex', gap: '8px' }}>
          <Button variant="outlined" onClick={handleTestNotification} disabled={testLoading || loading}>
            {testLoading ? 'Sending...' : 'Send Test Email'}
          </Button>
          <Button variant="contained" onClick={handleUpdateSetting}>
            Update Recipients
          </Button>
        </Grid>
      </Grid>
    </LoadingWrapper>
  );
};

export default NotificationSettingDetailPage;
